import styles from './LanguageSwitcher.module.scss'
import { Fragment } from 'react'
import classNames from 'classnames'
import { useStoreDispatch } from 'hooks/store'
import { useRouter } from 'next/router'
import { Language } from 'types/language'
import { updateInput } from 'store/customer/slice'
import { InputGroupKeys, PersonalDataFieldKeys } from 'store/customer/enums'
import { translateRoute } from 'utils/routes'
import { SUPPORTED_LANGUAGES } from 'constants/i18n'
import { LanguageSwitcherProps } from 'components/language-switcher/types'
import { Button } from '@boltenergy-be/design-system'
import { useGetLanguage } from 'hooks/useGetLanguage'

const LanguageSwitcher = ({ className, localized }: LanguageSwitcherProps) => {
  // Redux store
  const dispatch = useStoreDispatch()

  // Router
  const router = useRouter()
  const { asPath, pathname, query } = router

  // i18n
  const language = useGetLanguage()

  /**
   * Changes the language to the given one
   *
   * @param {Language} newLang
   */
  const changeLanguage = (newLang: Language) => {
    // Update in the registration store
    dispatch(updateInput({ group: InputGroupKeys.PERSONAL_DATA, key: PersonalDataFieldKeys.LANGUAGE, value: newLang }))

    // Just switch the route if homepage
    if (pathname === '/') {
      return router.push(pathname, asPath, { locale: newLang })
    }

    // Can be removed once summer 2025 campaign is over
    if (pathname.startsWith('/festivals')) {
      return router.push(pathname, asPath, { locale: newLang })
    }

    // Translate the route first before switching if not homepage
    const { asPath: translatedAsPath } = translateRoute(router, newLang)
    return router.push({ pathname, query }, translatedAsPath, { locale: newLang })
  }

  return (
    <nav className={classNames(styles['language-switcher'], className)}>
      {SUPPORTED_LANGUAGES.map((lang, index) => (
        <Fragment key={lang}>
          <Button
            className={styles.language}
            data-active={lang === language}
            representation="link"
            size="small"
            onClick={() => {
              if (!!localized && localized[lang]) return router.push({ pathname: localized[lang] }, localized[lang], { locale: false })
              if (language !== lang) return changeLanguage(lang as Language)
            }}
          >
            <abbr lang={lang} title={lang === 'fr' ? 'Français' : 'Nederlands'}>
              {lang.toUpperCase()}
            </abbr>
          </Button>
          {SUPPORTED_LANGUAGES.length !== index + 1 && <span>/</span>}
        </Fragment>
      ))}
    </nav>
  )
}

export default LanguageSwitcher
